import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from '@wix/thunderbolt-elements/src/providers/usePopper/usePopper';

import { getPopperConfig } from './utils';
import { getPopperConfig as getPopperConfigOLD } from './OLD_utils';

export type UseMenuPositioningOptions = {
  isMenuOpen: boolean;
  rootElement: HTMLDivElement | null;
  referenceElement: HTMLDivElement | null;
  popperElement: HTMLDivElement | null;
  useNewDropdownMenuAlignment: boolean;
};

export const useMenuPositioning = ({
  isMenuOpen,
  rootElement,
  referenceElement,
  popperElement,
  useNewDropdownMenuAlignment,
}: UseMenuPositioningOptions) => {
  const [isVisible, setIsVisible] = useState(false);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const popperConfig = useMemo(
    () =>
      useNewDropdownMenuAlignment
        ? getPopperConfig({
            rootElement,
            referenceElement,
            popperElement,
          })
        : getPopperConfigOLD({
            rootElement,
            referenceElement,
          }),
    [rootElement, referenceElement, popperElement, useNewDropdownMenuAlignment],
  );

  const { setPopper, setRef, styles, update } = usePopper(popperConfig);

  const destroyResizeObserver = useCallback(() => {
    if (resizeObserver.current) {
      resizeObserver.current.disconnect();
      resizeObserver.current = null;
    }
  }, []);

  const initResizeObserver = useCallback(() => {
    if (!useNewDropdownMenuAlignment || !rootElement || !update) {
      return;
    }

    if (resizeObserver.current) {
      return;
    }

    resizeObserver.current = new ResizeObserver(() => update());
    resizeObserver.current.observe(rootElement);
  }, [rootElement, update, useNewDropdownMenuAlignment]);

  const handleMenuOpen = useCallback(() => {
    if (!popperElement) {
      return;
    }

    setRef(referenceElement);
    setPopper(popperElement);
    setIsVisible(true);
  }, [popperElement, referenceElement, setPopper, setRef]);

  const handleMenuClose = useCallback(() => {
    setRef(null);
    setPopper(null);
    setIsVisible(false);
  }, [setPopper, setRef]);

  useEffect(() => {
    if (isMenuOpen) {
      handleMenuOpen();
      initResizeObserver();
    } else {
      handleMenuClose();
    }

    return () => {
      destroyResizeObserver();
    };
  }, [
    isMenuOpen,
    handleMenuOpen,
    handleMenuClose,
    destroyResizeObserver,
    initResizeObserver,
  ]);

  return {
    menuStyles: {
      ...styles.popper,
      left: undefined,
    },
    isVisible,
  };
};
